import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Label,
} from "@devhop/ui";
import { Loader2Icon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { parseYearMonthFromDateYyyyMmDd } from "@/modules/reports/domain/lib/parse-year-month";
import { useInstructorPayrollBatchByMonthQuery } from "@/modules/reports/presentation/api/queries";
import {
  useAssistInstructorsQuery,
  useCompleteRoomBooking,
  useRoomBookingQuery,
} from "../api/queries";

type ExtraInstructor = { id: string; name: string };

type CompleteRoomBookingDialogProps = {
  bookingId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

export function CompleteRoomBookingDialog({
  bookingId,
  open,
  onOpenChange,
  onSuccess,
}: CompleteRoomBookingDialogProps) {
  const completeBooking = useCompleteRoomBooking();
  const { data: assistData, isLoading: assistsLoading } =
    useAssistInstructorsQuery(open && bookingId ? bookingId : "");
  const { data: booking } = useRoomBookingQuery(
    open && bookingId ? bookingId : "",
  );

  const yearMonth = useMemo(() => {
    if (!booking?.date) return null;
    try {
      return parseYearMonthFromDateYyyyMmDd(booking.date);
    } catch {
      return null;
    }
  }, [booking?.date]);

  const payrollBatchQuery = useInstructorPayrollBatchByMonthQuery(
    yearMonth?.year ?? null,
    yearMonth?.month ?? null,
  );
  const payrollLocked = !!payrollBatchQuery.data;

  const existing = assistData?.data ?? [];

  /** check-in ids that will remain (attended) */
  const [keptCheckInIds, setKeptCheckInIds] = useState<Set<string>>(new Set());
  const [extra, setExtra] = useState<ExtraInstructor[]>([]);
  const [pickId, setPickId] = useState("");

  useEffect(() => {
    if (!open) {
      setKeptCheckInIds(new Set());
      setExtra([]);
      setPickId("");
    }
  }, [open]);

  useEffect(() => {
    if (!open || assistsLoading) return;
    setKeptCheckInIds(new Set((assistData?.data ?? []).map((r) => r.id)));
  }, [open, bookingId, assistsLoading, assistData?.data]);

  const existingInstructorIds = useMemo(
    () => new Set(existing.map((r) => r.instructorId)),
    [existing],
  );
  const extraIds = useMemo(() => new Set(extra.map((s) => s.id)), [extra]);

  const toggleExisting = (checkInId: string, checked: boolean) => {
    setKeptCheckInIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(checkInId);
      else next.delete(checkInId);
      return next;
    });
  };

  const handleAddExtra = async () => {
    if (!pickId || existingInstructorIds.has(pickId) || extraIds.has(pickId))
      return;
    const item = await hydrateLookupItem<{ id: string; name: string }>(
      `${config.apiUrl}/master/instructors/lookup`,
      pickId,
    );
    if (!item) return;
    setExtra((prev) => [...prev, { id: item.id, name: item.name }]);
    setPickId("");
  };

  const handleConfirm = async () => {
    if (!bookingId) return;
    const removeCheckInIds = payrollLocked
      ? []
      : existing
          .filter((r) => !keptCheckInIds.has(r.id))
          .map((r) => r.id);
    try {
      await completeBooking.mutateAsync({
        id: bookingId,
        removeCheckInIds,
        assistInstructorIds: payrollLocked ? [] : extra.map((s) => s.id),
      });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // toast in mutation
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="max-w-md gap-4 sm:max-w-md">
          <DialogTitle className="font-semibold text-lg">
            {"\u0eaa\u0eb3\u0ec0\u0ea5\u0eb1\u0e94\u0e81\u0eb2\u0e99\u0e88\u0ead\u0e87"}
          </DialogTitle>

          <p className="text-muted-foreground text-sm">
            {
              "\u0e84\u0ec8\u0eb2\u0ec0\u0e8a\u0ebb\u0ec8\u0eb2\u0e9a\u0ecd\u0ec8\u0e9b\u0ec8\u0ebd\u0e99 \u00b7 \u0e95\u0eb4\u0e81\u0e84\u0eb9\u0e97\u0eb5\u0ec8\u0ea1\u0eb2\u0e88\u0eb4\u0e87 \u2014 \u0e96\u0ec9\u0eb2\u0e9a\u0ecd\u0ec8\u0ea1\u0eb2\u0ec3\u0eab\u0ec9\u0e9b\u0eb4\u0e94\u0e95\u0eb4\u0e81\u0ead\u0ead\u0e81"
            }
          </p>

          
          {payrollLocked ? (
            <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-900 text-sm dark:text-amber-200">
              ປິດງວດ payroll ແລ້ວ — ບໍ່ສາມາດແກ້ຄູຊ່ວຍສອນໄດ້ (ຍັງສຳເລັດການຈອງໄດ້)
            </p>
          ) : null}

          <div className="space-y-2">
            <Label className="font-medium text-sm">
              {"\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0eaa\u0ead\u0e99"}
            </Label>

            {assistsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2Icon className="size-4 animate-spin" />
                {"\u0e81\u0eb3\u0ea5\u0eb1\u0e87\u0ec2\u0eab\u0ebc\u0e94..."}
              </div>
            ) : existing.length > 0 ? (
              <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-2">
                {existing.map((row) => (
                  <li key={row.id}>
                    <label
                      htmlFor={`assist-check-${row.id}`}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted"
                    >
                      <Checkbox
                        id={`assist-check-${row.id}`}
                        checked={keptCheckInIds.has(row.id)}
                        onCheckedChange={(v) =>
                          toggleExisting(row.id, v === true)
                        }
                        disabled={completeBooking.isPending || payrollLocked}
                      />
                      <span className="font-medium text-sm">
                        {row.instructorName}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-xs">
                {
                  "\u0e8d\u0eb1\u0e87\u0e9a\u0ecd\u0ec8\u0ea1\u0eb5\u0e84\u0eb9\u0e8a\u0ec8\u0ea7\u0e8d\u0e97\u0eb5\u0ec8\u0e9a\u0eb1\u0e99\u0e97\u0eb6\u0e81\u0ea5\u0ec8\u0ea7\u0e87\u0edc\u0ec9\u0eb2 \u2014 \u0ec0\u0e9e\u0eb5\u0ec8\u0ea1\u0ec4\u0e94\u0ec9\u0e94\u0ec9\u0eb2\u0e99\u0ea5\u0eb8\u0ec8\u0ea1"
                }
              </p>
            )}

            {!payrollLocked ? <div className="flex flex-col gap-2 pt-1 sm:flex-row">
              <div className="min-w-0 flex-1">
                <InfiniteCombobox<{ id: string; name: string }>
                  queryKey={[
                    "master",
                    "instructors",
                    "lookup",
                    "assist-complete",
                  ]}
                  queryFn={async (params: {
                    search: string;
                    pageParam: number;
                  }) => {
                    const limit = 20;
                    const skip = (params.pageParam - 1) * limit;
                    const url = new URL(
                      `${config.apiUrl}/master/instructors/lookup`,
                    );
                    url.searchParams.set("limit", String(limit));
                    url.searchParams.set("skip", String(skip));
                    if (params.search) url.searchParams.set("q", params.search);
                    return fetchLookupForInfinite<{ id: string; name: string }>(
                      url.toString(),
                      params,
                    );
                  }}
                  preloadQueryFn={(id) =>
                    hydrateLookupItem<{ id: string; name: string }>(
                      `${config.apiUrl}/master/instructors/lookup`,
                      id,
                    )
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  value={pickId}
                  onValueChange={setPickId}
                  placeholder={
                    "\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1\u0e84\u0eb9\u0ead\u0eb7\u0ec8\u0e99..."
                  }
                  className="w-full"
                  clearable
                />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={
                  !pickId ||
                  existingInstructorIds.has(pickId) ||
                  extraIds.has(pickId) ||
                  completeBooking.isPending
                }
                onClick={() => void handleAddExtra()}
              >
                {"\u0ec0\u0e9e\u0eb5\u0ec8\u0ea1"}
              </Button>
            </div> : null}

            {!payrollLocked && extra.length > 0 ? (
              <ul className="space-y-1">
                {extra.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-sm"
                  >
                    <span className="truncate font-medium">{s.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={completeBooking.isPending}
                      onClick={() =>
                        setExtra((prev) => prev.filter((x) => x.id !== s.id))
                      }
                      aria-label={`remove-${s.name}`}
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={completeBooking.isPending}
              onClick={() => onOpenChange(false)}
            >
              {"\u0e8d\u0ebb\u0e81\u0ec0\u0ea5\u0eb5\u0e81"}
            </Button>
            <Button
              type="button"
              disabled={!bookingId || completeBooking.isPending}
              onClick={() => void handleConfirm()}
            >
              {completeBooking.isPending ? (
                <Loader2Icon className="mr-2 size-4 animate-spin" />
              ) : null}
              {"\u0eaa\u0eb3\u0ec0\u0ea5\u0eb1\u0e94"}
            </Button>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
